import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { revenueApi } from "@/lib/api";
import { POLLING_INTERVAL } from "@/hooks/useInterval";
import type { RoyaltyRunPayload, RoyaltyStatementBatch } from "@/types";

interface UseCreateRoyaltyRunOptions {
  onSuccess?: (batch: RoyaltyStatementBatch) => void;
  onError?: (error: Error) => void;
}

/** Starts a run. Generation happens in the background — poll with useRoyaltyBatch. */
export function useCreateRoyaltyRun(options: UseCreateRoyaltyRunOptions = {}) {
  const queryClient = useQueryClient();

  const { mutate, isPending, error, reset } = useMutation({
    mutationFn: (payload: RoyaltyRunPayload) => revenueApi.createRoyaltyRun(payload),
    onSuccess: (batch) => {
      queryClient.invalidateQueries({ queryKey: ["royalty-statements"] });
      options.onSuccess?.(batch);
    },
    onError: (error: Error) => options.onError?.(error),
  });

  return { createRoyaltyRun: mutate, isPending, error, reset };
}

/**
 * Polls a batch until generation finishes (completed, or partial with failures).
 * The statements list polls itself while any statement is still generating, so
 * the two stay in step without this hook touching the list cache.
 */
export function useRoyaltyBatch(batchId: string | null | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: ["royalty-batch", batchId],
    queryFn: () => revenueApi.getRoyaltyStatementBatch(batchId as string),
    enabled: !!batchId,
    refetchInterval: (query) =>
      query.state.data?.status === "generating" ? POLLING_INTERVAL : false,
  });

  return { batch: data ?? null, isLoading };
}

/** Retries only the statements that failed in the batch. */
export function useRetryRoyaltyBatch() {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (batchId: string) => revenueApi.retryRoyaltyStatementBatch(batchId),
    onSuccess: (batch) => {
      queryClient.setQueryData(["royalty-batch", batch.id], batch);
      queryClient.invalidateQueries({ queryKey: ["royalty-statements"] });
    },
  });

  return { retryBatch: mutate, isPending };
}

interface UseCancelRoyaltyBatchOptions {
  onSuccess?: (batch: RoyaltyStatementBatch) => void;
  onError?: (error: Error) => void;
}

/** Stops a run part-way, keeping what it has already generated. */
export function useCancelRoyaltyBatch(options: UseCancelRoyaltyBatchOptions = {}) {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (batchId: string) => revenueApi.cancelRoyaltyStatementBatch(batchId),
    onSuccess: (batch) => {
      queryClient.setQueryData(["royalty-batch", batch.id], batch);
      queryClient.invalidateQueries({ queryKey: ["royalty-statements"] });
      options.onSuccess?.(batch);
    },
    onError: (error: Error) => options.onError?.(error),
  });

  return { cancelBatch: mutate, isPending };
}

interface UseUndoRoyaltyBatchOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/** Deletes every statement a run produced. */
export function useUndoRoyaltyBatch(options: UseUndoRoyaltyBatchOptions = {}) {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (batchId: string) => revenueApi.undoRoyaltyStatementBatch(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["royalty-statements"] });
      queryClient.invalidateQueries({ queryKey: ["royalty-batch"] });
      options.onSuccess?.();
    },
    onError: (error: Error) => options.onError?.(error),
  });

  return { undoBatch: mutate, isPending };
}
